import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, AlertTriangle, ExternalLink, Unplug, RefreshCw, History, Stethoscope } from 'lucide-react';
import SquareDiagnosticsDialog from './SquareDiagnosticsDialog';

/**
 * Square OAuth connect card. Shows Not connected / Connected (with merchant name) /
 * Reauthorization required states.
 *
 * On mount, checks ?square=connected|denied|error query params (from the OAuth
 * callback redirect) and surfaces a toast. Then fetches /api/square/status.
 */
export default function SquareIntegrationCard() {
  const { api, currentLocation } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [recentRuns, setRecentRuns] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);

  const load = useCallback(async () => {
    if (!currentLocation) return;
    setLoading(true);
    try {
      const res = await api.get('/square/status', { params: { location_id: currentLocation.id } });
      setStatus(res.data);
      if (res.data?.connected) {
        try {
          const logRes = await api.get('/square/sync-runs', { params: { location_id: currentLocation.id, limit: 10 } });
          setRecentRuns(logRes.data?.items || []);
        } catch { /* non-fatal */ }
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load Square status');
    } finally {
      setLoading(false);
    }
  }, [api, currentLocation]);

  useEffect(() => {
    // Surface callback query params from Square OAuth redirect.
    const params = new URLSearchParams(window.location.search);
    const square = params.get('square');
    const detail = params.get('detail');
    if (square === 'connected') toast.success(`Connected to ${detail || 'Square'}`);
    else if (square === 'denied') toast.error(`Square declined: ${detail || 'authorization denied'}`);
    else if (square === 'error') toast.error(`Square connection failed: ${detail || 'unknown error'}`);
    if (square) {
      params.delete('square');
      params.delete('detail');
      const clean = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
      window.history.replaceState({}, '', clean);
    }
    load();
  }, [load]);

  const connect = async () => {
    if (!currentLocation) return toast.error('Select a location first');
    setBusy(true);
    try {
      const res = await api.post('/square/connect', { location_id: currentLocation.id });
      window.location.assign(res.data.authorization_url);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to start Square connect');
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Square? Existing data will be preserved but new data will stop syncing.')) return;
    setBusy(true);
    try {
      await api.post('/square/disconnect', { location_id: currentLocation.id });
      toast.success('Disconnected from Square');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Disconnect failed');
    } finally {
      setBusy(false);
    }
  };

  const syncNow = async ({ backfill = false } = {}) => {
    if (!currentLocation) return;
    const label = backfill ? 'Backfill (12 months)' : 'Sync';
    if (backfill && !window.confirm('Backfill will pull the last 12 months of Square data. This may take a few minutes. Continue?')) return;
    setSyncing(true);
    try {
      const res = await api.post('/square/sync-now', { location_id: currentLocation.id, backfill });
      const r = res.data?.result || {};
      if (r.error) {
        toast.error(`${label} failed: ${r.error}`);
      } else {
        const bits = ['orders', 'catalog', 'team_members', 'timecards']
          .filter((k) => (r[k] ?? 0) > 0)
          .map((k) => `${k.replace('_', ' ')} ${r[k]}`);
        toast.success(`${label} complete${bits.length ? ` — ${bits.join(', ')}` : ' (no new records)'}`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${label} failed`);
    } finally {
      setSyncing(false);
    }
  };

  const fmtDateTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    } catch { return iso; }
  };

  return (
    <Card data-testid="square-integration-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#006AFF' }} />
            Square
            <Badge variant="outline" className="text-[10px]" data-testid="square-env-badge">
              {status?.environment || 'sandbox'}
            </Badge>
          </CardTitle>
          {status?.connected && (
            <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white" data-testid="square-connected-badge">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
            </Badge>
          )}
          {status?.status === 'reauthorization_required' && (
            <Badge className="bg-amber-500 hover:bg-amber-500 text-white" data-testid="square-reauth-badge">
              <AlertTriangle className="w-3 h-3 mr-1" /> Reconnect required
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="py-6 flex justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : status?.connected ? (
          <div className="space-y-2 text-sm" data-testid="square-connection-info">
            <div className="flex justify-between"><span className="text-muted-foreground">Merchant</span><span className="font-medium">{status.merchant_name}</span></div>
            {status.country && <div className="flex justify-between"><span className="text-muted-foreground">Country</span><span>{status.country}</span></div>}
            {status.currency && <div className="flex justify-between"><span className="text-muted-foreground">Currency</span><span>{status.currency}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">Connected</span><span>{status.connected_at?.slice(0, 10)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Last synced</span><span data-testid="square-last-synced">{fmtDateTime(status.last_synced_at)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Access token expires</span><span>{status.expires_at?.slice(0, 10) || '—'}</span></div>
            <div className="text-xs text-muted-foreground pt-1">
              Scopes: {status.scopes?.join(', ')}
            </div>
            {status.has_inventory_write === false && (
              <div className="mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs" data-testid="square-inventory-reconnect-notice">
                <div className="flex items-center gap-1 font-medium text-amber-800">
                  <AlertTriangle className="w-3 h-3" /> Reconnect to enable stock management
                </div>
                <p className="text-amber-700 mt-1">
                  Your connection is missing <code className="font-mono">INVENTORY_READ</code> / <code className="font-mono">INVENTORY_WRITE</code>. Click <b>Reconnect</b> below — historical data is preserved.
                </p>
              </div>
            )}
            <div className="text-xs text-muted-foreground pt-1">
              Automatic daily sync is enabled. Use <b>Sync Now</b> to pull the latest data on demand,
              or <b>Backfill</b> to re-pull the last 12 months.
            </div>
            {recentRuns.length > 0 && (
              <div className="pt-2">
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                  onClick={() => setShowLog((v) => !v)}
                  data-testid="toggle-sync-log-btn"
                >
                  <History className="w-3 h-3" />
                  {showLog ? 'Hide' : 'Show'} recent sync runs ({recentRuns.length})
                </button>
                {showLog && (
                  <div className="mt-2 border rounded divide-y text-xs" data-testid="square-sync-log">
                    {recentRuns.map((r, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-1.5">
                        <span className="capitalize">{r.resource?.replace('_', ' ')}</span>
                        <span className="text-muted-foreground">{fmtDateTime(r.started_at)}</span>
                        <span className={r.status === 'ok' ? 'text-emerald-600' : r.status === 'error' ? 'text-red-600' : 'text-amber-600'}>
                          {r.status === 'ok' ? `${r.records_upserted ?? 0} recs` : (r.error?.slice(0, 40) || r.status)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Connect your Square account to pull sales, orders, product mix, staff and timecards
              automatically. No CSV exports needed. You&apos;ll be redirected to Square to grant access.
            </p>
            <p className="text-xs text-muted-foreground">
              Requesting read-only scopes: <code className="font-mono">MERCHANT_PROFILE_READ · ORDERS_READ · PAYMENTS_READ · ITEMS_READ · EMPLOYEES_READ · TIMECARDS_READ</code>
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2">
          {status?.connected ? (
            <>
              <Button onClick={() => syncNow({ backfill: false })} disabled={syncing || busy} data-testid="square-sync-now-btn">
                {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Sync Now
              </Button>
              <Button variant="outline" onClick={() => syncNow({ backfill: true })} disabled={syncing || busy} data-testid="square-backfill-btn">
                Backfill 12 months
              </Button>
              {status.has_inventory_write === false && (
                <Button variant="secondary" onClick={connect} disabled={busy || syncing} data-testid="square-reconnect-btn">
                  <ExternalLink className="w-4 h-4 mr-1" /> Reconnect for inventory
                </Button>
              )}
              <Button variant="outline" onClick={load} disabled={busy || syncing} data-testid="refresh-square-status">
                Refresh status
              </Button>
              <Button variant="outline" onClick={() => setDiagOpen(true)} disabled={busy || syncing} data-testid="square-diagnostics-btn">
                <Stethoscope className="w-4 h-4 mr-1" /> Diagnostics
              </Button>
              <Button variant="ghost" className="text-red-600 hover:text-red-700" onClick={disconnect} disabled={busy || syncing} data-testid="square-disconnect-btn">
                <Unplug className="w-4 h-4 mr-1" /> Disconnect
              </Button>
            </>
          ) : (
            <>
              <Button onClick={connect} disabled={busy || !currentLocation} data-testid="square-connect-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-1" />}
                Connect Square
              </Button>
              <Button variant="outline" onClick={() => setDiagOpen(true)} disabled={!currentLocation} data-testid="square-diagnostics-btn">
                <Stethoscope className="w-4 h-4 mr-1" /> Diagnostics
              </Button>
            </>
          )}
        </div>
      </CardContent>
      <SquareDiagnosticsDialog open={diagOpen} onOpenChange={setDiagOpen} />
    </Card>
  );
}
